import { useCallback } from 'react';
import { deliveryNotesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { DeliveryNote } from '../types';

export function useDeliveryNotes(
  options?: ResourceQueryOptions
): ResourceState<DeliveryNote> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      deliveryNotesService.getAll(opts) as unknown as Promise<{ data: DeliveryNote[]; meta?: any }>,
    []
  );
  return useResourceCollection<DeliveryNote>(fetchFn, options);
}

export function useDeliveryNote(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<DeliveryNote> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (deliveryNoteId: number, opts?: ResourceQueryOptions) =>
      deliveryNotesService.getById(deliveryNoteId, opts) as unknown as Promise<DeliveryNote>,
    []
  );
  return useResourceById<DeliveryNote>(fetchFn, numericId, options);
}

export function useCreateDeliveryNote(
  onSuccess?: (data: DeliveryNote) => void
): MutationState<DeliveryNote> {
  const createFn = useCallback(
    (data: any) => deliveryNotesService.create(data) as unknown as Promise<DeliveryNote>,
    []
  );
  return useCreateResource<DeliveryNote>(createFn, onSuccess);
}

export function useUpdateDeliveryNote(
  onSuccess?: (data: DeliveryNote) => void
): MutationState<DeliveryNote> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return deliveryNotesService.update(numericId, data) as unknown as Promise<DeliveryNote>;
    },
    []
  );
  return useUpdateResource<DeliveryNote>(updateFn, onSuccess);
}

/**
 * Delete delivery note using documentId
 * @param data - Full delivery note object containing documentId
 */
export function useDeleteDeliveryNote(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return deliveryNotesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

